import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { db } from '../../lib/firebase.js';
import { collection, getDocs, query, where } from 'firebase/firestore';

function AdminUsers() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [getToken]);

  useEffect(() => {
    const filtered = users.filter(user =>
      `${user.id} ${user.email || ''} ${user.name || ''}`.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredUsers(filtered);
    setPage(0);
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const directoryResponse = await fetch('/api/admin?view=users', { headers: { Authorization: `Bearer ${token}` } });
      if (!directoryResponse.ok) throw new Error('Unable to load the Clerk user directory');
      const directoryUsers = (await directoryResponse.json()).users;

      // Aggregate user data from stories and characters
      const storiesSnapshot = await getDocs(collection(db, 'stories'));
      const charactersSnapshot = await getDocs(collection(db, 'characters'));

      const stories = storiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const characters = charactersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Build user map with aggregated stats
      const userMap = new Map();

      stories.forEach(story => {
        if (!story.user_id) return;
        if (!userMap.has(story.user_id)) {
          userMap.set(story.user_id, {
            id: story.user_id,
            storiesCount: 0,
            charactersCount: 0,
            lastActivity: null,
            firstActivity: null,
          });
        }
        const user = userMap.get(story.user_id);
        user.storiesCount++;
        const storyDate = new Date(story.created_at);
        if (!user.lastActivity || storyDate > user.lastActivity) {
          user.lastActivity = storyDate;
        }
        if (!user.firstActivity || storyDate < user.firstActivity) {
          user.firstActivity = storyDate;
        }
      });

      characters.forEach(char => {
        if (!char.user_id) return;
        if (!userMap.has(char.user_id)) {
          userMap.set(char.user_id, {
            id: char.user_id,
            storiesCount: 0,
            charactersCount: 0,
            lastActivity: null,
            firstActivity: null,
          });
        }
        const user = userMap.get(char.user_id);
        user.charactersCount++;
        const charDate = new Date(char.created_at);
        if (!user.lastActivity || charDate > user.lastActivity) {
          user.lastActivity = charDate;
        }
        if (!user.firstActivity || charDate < user.firstActivity) {
          user.firstActivity = charDate;
        }
      });

      directoryUsers.forEach(account => {
        const current = userMap.get(account.id) || { id: account.id, storiesCount: 0, charactersCount: 0, lastActivity: null, firstActivity: null };
        userMap.set(account.id, {
          ...current,
          email: account.email,
          name: account.name,
          tier: account.publicMetadata?.subscriptionTier || 'free',
          status: account.publicMetadata?.subscriptionStatus || 'none',
          isAdmin: account.isAdmin,
        });
      });

      const userList = Array.from(userMap.values()).sort(
        (a, b) => (b.lastActivity || 0) - (a.lastActivity || 0)
      );

      setUsers(userList);
      setFilteredUsers(userList);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, user) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleViewDetails = async () => {
    handleMenuClose();
    setDetailsOpen(true);
    setLoadingDetails(true);

    try {
      // Fetch user's stories
      const storiesQuery = query(
        collection(db, 'stories'),
        where('user_id', '==', selectedUser.id)
      );
      const storiesSnapshot = await getDocs(storiesQuery);
      const userStories = storiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Fetch user's characters
      const charactersQuery = query(
        collection(db, 'characters'),
        where('user_id', '==', selectedUser.id)
      );
      const charactersSnapshot = await getDocs(charactersQuery);
      const userCharacters = charactersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setUserDetails({
        ...selectedUser,
        stories: userStories.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        characters: userCharacters,
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTierChip = (storiesCount, tier, isAdmin) => {
    if (isAdmin) return <Chip label="Admin" size="small" color="secondary" />;
    if (tier) return <Chip label={tier} size="small" variant="outlined" />;
    // Estimate tier based on activity
    if (storiesCount >= 30) return <Chip label="Premium+" size="small" color="secondary" />;
    if (storiesCount >= 10) return <Chip label="Premium" size="small" color="warning" />;
    if (storiesCount >= 5) return <Chip label="Basic" size="small" color="success" />;
    return <Chip label="Free" size="small" variant="outlined" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          User Management ({users.length} users)
        </Typography>
        <TextField
          size="small"
          placeholder="Search by user ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell align="center">Stories</TableCell>
                <TableCell align="center">Characters</TableCell>
                <TableCell>First Activity</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Est. Tier</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                          <PersonIcon sx={{ fontSize: 18 }} color="primary" />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: 11 }}>
                          {user.email || user.name || `${user.id.substring(0, 20)}...`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<AutoStoriesIcon sx={{ fontSize: 14 }} />}
                        label={user.storiesCount}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">{user.charactersCount}</TableCell>
                    <TableCell>
                      {user.firstActivity
                        ? user.firstActivity.toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {user.lastActivity
                        ? user.lastActivity.toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>{getTierChip(user.storiesCount, user.tier, user.isAdmin)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, user)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchQuery ? 'No users found matching your search' : 'No users found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
          View Details
        </MenuItem>
      </Menu>

      {/* User Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : userDetails ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      User ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                      {userDetails.id}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="h4" color="primary">
                          {userDetails.storiesCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Stories
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="h4" color="success.main">
                          {userDetails.charactersCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Characters
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body1">
                          {userDetails.firstActivity?.toLocaleDateString() || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          First Activity
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body1">
                          {userDetails.lastActivity?.toLocaleDateString() || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last Activity
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Recent Stories ({userDetails.stories.length})
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {userDetails.stories.slice(0, 10).map((story) => (
                      <ListItem key={story.id} divider>
                        <ListItemText
                          primary={story.title || 'Untitled'}
                          secondary={new Date(story.created_at).toLocaleDateString()}
                        />
                      </ListItem>
                    ))}
                    {userDetails.stories.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No stories" />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Characters ({userDetails.characters.length})
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {userDetails.characters.map((char) => (
                      <ListItem key={char.id} divider>
                        <ListItemText
                          primary={char.name}
                          secondary={char.age ? `Age: ${char.age}` : undefined}
                        />
                      </ListItem>
                    ))}
                    {userDetails.characters.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No characters" />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminUsers;
